<template>
    <div id="portfolio-wrap" :class="{ visible: ready }">
        <div id="images">
            <div
                v-for="(project, index) in projects"
                :key="`${project.title}-${index}`"
                class="image-item"
                :class="{ 'is-active': draggableIndex == index }"
            >
                <div class="mask">
                    <div class="image-container">
                        <template v-if="!project.media.embed">
                            <img :src="project.media.media" />
                        </template>
                        <template v-else>
                            <VimeoProjectComponent
                                :json="project.media"
                                :scroll-trigger="false"
                                :is-active="draggableIndex == index"
                                :project-slider="true"
                            >
                            </VimeoProjectComponent>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <ul id="portfolio" class="portfolio">
            <li
                v-for="(project, index) in projects"
                :key="`${project.title}-${index}`"
                :class="{ active: draggableIndex == index }"
            >
                <a :href="project.url">
                    <span>{{ project.title }}</span>
                    <span class="description">{{ project.description }}</span>
                </a>
            </li>
        </ul>
    </div>
</template>

<script>
import VimeoProjectComponent from './vimeo-project-component.vue';

import { gsap, ScrollTrigger, Draggable, Linear } from 'gsap/all';
import { InertiaPlugin } from '../gsap-bonus/InertiaPlugin';

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(Draggable);
gsap.registerPlugin(InertiaPlugin);

export default {
    name: 'ProjectSliderComponent',

    props: {
        initialProjects: {
            required: true,
            type: Array,
        },
    },

    components: {
        VimeoProjectComponent,
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            swiperSlider: null,
            draggableIndex: 3,
            activeIndex: 3,
            ready: false,
        };
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.dragging = false;
        this.init();

        window.addEventListener('resize', this.init);
    },

    computed: {
        projects() {
            // const last = this.initialProjects.slice(-3);
            // console.log(
            //     [
            //         ...last,
            //         ...this.initialProjects.slice(
            //             3,
            //             this.initialProjects.length
            //         ),
            //     ].length
            // );
            return this.initialProjects;
        },
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        init() {
            var wrapper = document.querySelector('.portfolio');
            var boxes = [...document.querySelectorAll('.portfolio li')];
            if (boxes.length < 1) {
                window.removeEventListener('resize', this.init);
                return;
            }

            var boxHeightValue = document.querySelector('.portfolio li');
            var boxHeight = boxHeightValue.clientHeight;
            var wrapHeight = boxes.length * boxHeight;

            this.initialProjectIndex = 0;
            this.items = boxes.map((item, index) => {
                const box = item.getBoundingClientRect();

                return {
                    el: item,
                    width: box.width,
                    height: box.height,
                };
            });

            this.sizes = this.items.reduce(
                (t, e) => ({
                    totalHeight: t.totalHeight + e.height,
                    maxItemWidth: Math.max(t.maxItemWidth, e.width),
                    maxItemHeight: Math.max(t.maxItemHeight, e.height),
                }),
                {
                    totalHeight: 0,
                    maxItemWidth: 0,
                    maxItemHeight: 0,
                }
            );

            const { maxItemWidth, maxItemHeight } = this.sizes;

            let i = 0;
            this.items.forEach((item) => {
                item.xCenter =
                    maxItemWidth + (window.innerWidth - item.width) / 2 - i;
                item.yCenter =
                    maxItemHeight +
                    (wrapper.getBoundingClientRect().height - item.height) / 2 -
                    i;
                i += item.height;
            });

            for (var j = 0; j < boxes.length; j++) {
                let box = boxes[j];
                gsap.set(box, { y: j * boxHeight, top: -boxHeight });
            }

            var wrapProgress = gsap.utils.wrap(0, 1);
            var snapBox = gsap.utils.snap(boxHeight);
            var clickAnimation = gsap.set({}, {});
            var proxy = document.createElement('div');
            var props = gsap.getProperty(proxy);

            var animation = gsap
                .to('.portfolio li', {
                    duration: 5,
                    y: '+=' + wrapHeight,
                    ease: Linear.easeNone,
                    paused: true,
                    repeat: -1,
                    modifiers: {
                        y: function (y, target) {
                            y = parseFloat(y) % wrapHeight;
                            return y + 'px';
                        },
                    },
                })
                .progress(1 / boxes.length);

            const snapFunc = (t) => {
                const initialCenter =
                    this.items[this.initialProjectIndex].yCenter;
                const r = t + initialCenter;
                const n = this.items[0];
                const i = this.items[this.items.length - 1];
                const a = n.yCenter + n.height / 2;
                const o = i.yCenter - i.height / 2;

                let s = 0;
                if (r > a) {
                    for (; r + s > a; ) s -= this.sizes.totalHeight;
                } else if (r < o) {
                    for (; r + s < o; ) s += this.sizes.totalHeight;
                }

                const c = r + s;
                const h = this.items.findIndex(
                    (t) =>
                        t.yCenter - t.height / 2 < c &&
                        t.yCenter + t.height / 2 > c
                );

                let newIndex = h + 3;
                if (newIndex > this.items.length - 1) {
                    newIndex = newIndex - this.items.length;
                }

                this.activeIndex = newIndex;

                return snapBox(t);
            };

            var that = this;
            const draggable = Draggable.create(proxy, {
                type: 'y',
                trigger: '#portfolio-wrap',
                onDrag: updateProgress,
                onDragStart: function () {
                    that.dragging = true;
                },
                onDragEnd: function () {
                    that.dragging = false;
                    gsap.delayedCall(this.tween.duration() - 1, () => {
                        that.draggableIndex = that.activeIndex;
                    });
                },
                inertia: true,
                onThrowUpdate: updateProgress,

                snap: {
                    y: snapFunc,
                },
            });

            function updateProgress(tween) {
                // console.log(InertiaPlugin.getVelocity(tween.target));
                animation.progress(wrapProgress(props('y') / wrapHeight));
            }

            this.activeIndex = this.initialProjectIndex + 3;
            this.draggableIndex = this.initialProjectIndex + 3;
            updateProgress();

            this.ready = true;

            this.goUp = () => {
                if (clickAnimation.isActive()) return;
                animateCarousel(1);
                this.activeIndex = this.activeIndex - 1;
                if (this.activeIndex < 0) {
                    this.activeIndex = this.items.length - 1;
                }
                this.draggableIndex = this.activeIndex;
            };

            this.goDown = () => {
                if (clickAnimation.isActive()) return;
                animateCarousel(-1);
                this.activeIndex = this.activeIndex + 1;
                if (this.activeIndex > this.items.length - 1) {
                    this.activeIndex = 0;
                }
                this.draggableIndex = this.activeIndex;
            };

            function animateCarousel(direction) {
                clickAnimation.kill();

                clickAnimation = gsap.to(proxy, {
                    duration: 0.8,
                    y: snapBox(props('y') + direction * boxHeight),
                    onUpdate: updateProgress,
                });
            }

            // this.onKeyDownDebounced = this.handleKeyDown.bind(this);
            document.addEventListener('keydown', this.handleKeyDown);
            this.listenForScroll();
        },

        listenForScroll() {
            this.lethargy = new Lethargy(20, 120, 0.05);

            // Cross-browser way to bind to mouse events
            window.addEventListener('mousewheel', this.onUserScroll);
            window.addEventListener('DOMMouseScroll', this.onUserScroll);
            window.addEventListener('wheel', this.onUserScroll);
            window.addEventListener('MozMousePixelScroll', this.onUserScroll);
        },

        removeScrollListener() {
            this.lethargy = null;

            // Cross-browser way to bind to mouse events
            window.removeEventListener('mousewheel', this.onUserScroll);
            window.removeEventListener('DOMMouseScroll', this.onUserScroll);
            window.removeEventListener('wheel', this.onUserScroll);
            window.removeEventListener(
                'MozMousePixelScroll',
                this.onUserScroll
            );
        },

        goToProject(index) {
            if (this.projects[index]) {
                page(this.projects[index].url);
            }
        },

        onUserScroll: _.throttle(function (e) {
            e.stopPropagation();

            if (!this.lethargy) {
                return;
            }

            if (this.scrolling || this.dragging) {
                return;
            }

            this.scrolling = true;
            gsap.delayedCall(0.6, () => {
                this.scrolling = false;
            });

            const result = this.lethargy.check(e);

            // false means it's not a scroll intent, 1 or -1 means it is
            if (result === -1) {
                this.goDown();
            } else if (result === 1) {
                this.goUp();
            }
        }),

        handleKeyDown: _.throttle(function (event) {
            if (event.key === 'ArrowUp' || event.key === 'Up') {
                this.goUp();
                return;
            }

            if (event.key === 'ArrowDown' || event.key === 'Down') {
                this.goDown();
                return;
            }

            if (event.key === 'Enter') {
                this.goToProject(this.draggableIndex);
                return;
            }
        }, 500),
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.init);
        document.removeEventListener('keydown', this.handleKeyDown);
        this.removeScrollListener();
    },
};
</script>

<style lang="scss" scoped>
$title-font: 'CabinetGrotesk-Variable';

#portfolio-wrap {
    height: 100vh;
    width: 100%;
    overflow: hidden;
    margin: 0 auto;
    text-align: center;
    font-size: 48px;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transform: opacity 0.8s ease;
    &.visible {
        opacity: 1;
    }
}

#images {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
}

.image-item {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

#portfolio-wrap ul {
    margin: 0;
    padding: 0;
    overflow: hidden;
    z-index: 1;
    height: 120vh;
    position: absolute;
    top: -10vh;
    left: 0;
    width: 100%;
}

#portfolio-wrap ul li {
    margin: 0px;
    padding: 0;
    list-style: none;
    position: absolute;
    width: 100%;
    line-height: 1;
    height: 24vh;
    font-family: $title-font;
    font-weight: 200;
    font-size: 40px;
    transition: font-weight 0.8s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    @media only screen and (min-width: 768px) {
        font-size: 4.861111111111112vw;
        line-height: 1;
    }

    img {
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 30vw;
        height: auto;
        opacity: 0;
        transition: opacity 0.8s ease;
    }

    a {
        cursor: none;
        max-width: 70vw;
        line-height: 1;
        color: #fff;
    }

    span {
        display: block;
        transform: scale(0.8);
        transition: transform 0.8s ease;
        cursor: pointer;
    }

    span.description {
        display: none;

        @media only screen and (min-width: 768px) {
            display: block;
            transform: scale(1) translateY(15px);
            font-size: 32px;
            margin-top: 10px;
            font-weight: 200;
            opacity: 0;
            transition: transform 0.8s ease, opacity 0.8s ease;
        }

        @media only screen and (min-width: 1200px) {
            font-size: 2.5vw;
        }
    }

    &.active {
        font-weight: 900;
        transform: scale(1);

        img {
            opacity: 1;
        }

        span {
            transform: scale(1);
        }

         span.description {
            transform: scale(1) translateY(0px);
            opacity: 1;
        }
    }
}

#left {
    position: absolute;
    z-index: 10;
    left: 10px;
    top: 10px;
}

#right {
    position: absolute;
    z-index: 10;
    right: 10px;
    top: 10px;
}

.mask {
    overflow: hidden;
    transform: scale(0.85);
    opacity: 0;
    transition: opacity 0.25s ease,
        transform 1.5s cubic-bezier(0.23, 1, 0.32, 1),
        -webkit-transform 1.5s cubic-bezier(0.23, 1, 0.32, 1);

    .is-active & {
        opacity: 1;
        transform: scale(1);
        -webkit-transform: scale(1);
    }
}

.image-container {
    transform: scale(1.5);
    transition: transform 0ms 0.25s, -webkit-transform 0ms 0.25s;
    width: 80vw;
    aspect-ratio: 16 / 9;

    @media screen and (min-width: 1280px) {
        width: 70vw;
    }

    .is-active & {
        transform: scale(1);
        -webkit-transform: scale(1);
        transition: transform 1.5s cubic-bezier(0.23, 1, 0.32, 1),
            -webkit-transform 1.5s cubic-bezier(0.23, 1, 0.32, 1);
    }

    img {
        display: block;
        width: 100%;
        height: 100%;
    }
}

.inline-vimeo-wrapper {
    position: relative;
    pointer-events: none;
    width: 100%;
    margin: 0 auto;
    width: 80vw;
    aspect-ratio: 16 / 9;

    @media screen and (min-width: 1280px) {
        width: 70vw;
    }
}

.inline-vimeo-inner {
    position: relative;
    max-width: 100%;
    padding-bottom: 100%;

    iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
}
</style>
